import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Button,
  Box,
  InputAdornment,
  TextField,
  Skeleton,
  ThemeProvider,
  Toolbar,
  createTheme,
  Modal,
  Fade,
  IconButton,
  Pagination,
  Autocomplete,
  Popper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
} from "@mui/material";
import {
  CameraAlt,
  CameraFront,
  CameraRear,
  Close,
  DirectionsCarFilledOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowRight,
  Photo,
  Search,
} from "@mui/icons-material";
import "react-toastify/dist/ReactToastify.css";
import {
  GetVeiculosTransferencia,
  Local,
  Transferencia,
} from "../../types/vehicleData";
import {
  getTransferVehicles,
  searchVehiclesTransfer,
  transferLocalVehicle,
  transferVehicle,
  uploadCapturedImages,
  uploadSignedTransfer,
} from "../../api/vehicleApi";
import { toast } from "react-toastify";
import { formatDocument } from "../../services/formatDocument";
import CanvasDraw from "react-canvas-draw";
import Webcam from "react-webcam";
import { getEmpresaLocais } from "../../api/dashboardApi";
import { AxiosError } from "axios";

const VehicleTransfer: React.FC = () => {
  const token = localStorage.getItem("token");
  const [vehicles, setVehicles] = useState<GetVeiculosTransferencia[]>([]);
  const webcamRef = useRef<Webcam>(null);
  const [originalData, setOriginalData] = useState<GetVeiculosTransferencia[]>(
    []
  );
  const [totalVehicles, setTotalVehicles] = useState<number | null>(null);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const canvasRef = useRef<any>(null);
  const [nomeTransferencia, setNomeTransferencia] = useState("");
  const [documentoTransferencia, setDocumentoTransferencia] = useState("");
  const [isSignOpen, setIsSignOpen] = useState(false);
  const [signed, setSigned] = useState<string>("");
  const [transferModal, setTransferModal] = useState<boolean>(false);
  const [transferModalHist, setTransferModalHist] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [descricao, setDescricao] = useState("");
  const [selectedVehicle, setSelectedVehicle] =
    useState<GetVeiculosTransferencia | null>(null);
  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Local | null>(null);
  const [isFrontFacing, setIsFrontFacing] = useState(true);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [historico, setHistorico] = useState<any[]>([]);
  const [openModalHistoricoFotos, setOpenModalHistoricoFotos] = useState(false);
  const [historicoFotos, setHistoricoFotos] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const defaultTheme = createTheme({
    typography: {
      fontFamily: "Poppins, sans-serif",
    },
  });

  const CustomPopper = (props: any) => {
    return <Popper {...props} placement="top" />;
  };

  const userDataString = localStorage.getItem("userData");
  const userData = userDataString ? JSON.parse(userDataString) : null;

  const shouldShowTransferButton =
    userData && userData.tipo_transferencia === "Tipo_Completo";

  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchData = async (page: number = 1) => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      setLoading(true);

      try {
        if (!token) throw new Error("Token de autenticação não encontrado");

        const data = await getTransferVehicles(token, page);
        setVehicles(Array.isArray(data.data) ? data.data : []);
        setOriginalData(Array.isArray(data.data) ? data.data : []);
        setCurrentPage(data.current_page);
        setTotalPages(data.last_page);
        setTotalVehicles(data.total);
      } catch (error) {
        toast.error("Erro ao buscar veículos em transferência!");
        console.error("Erro ao buscar veículos em transferência!:", error);
        setVehicles([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData(currentPage);
  }, [token, currentPage]);

  const handleExport = () => {
    const base64 = canvasRef.current.canvasContainer.childNodes[1].toDataURL();

    setSigned(base64);
    setIsSignOpen(false);
    toast.success("Assinatura salva com sucesso!");
  };

  const handleOpenModal = (vehicle: GetVeiculosTransferencia) => {
    setSelectedVehicle(vehicle);
    setTransferModal(true);
  };

  const handleCloseModal = () => {
    setTransferModal(false);
    setSelectedVehicle(null);
  };

  const handleOpenSign = () => {
    if (signed) {
      setIsSignOpen(true);
    } else {
      setIsSignOpen(true);
    }
  };

  const handleClearSignature = () => {
    setSigned("");
    setIsSignOpen(false);
  };

  const handleCloseSign = () => {
    setIsSignOpen(false);
    if (!signed) {
      handleClearSignature();
    }
  };

  const handleRowClick = (index: number) => {
    setOpenRow(openRow === index ? null : index);
  };

  const handleDocumentoChange = (event: any) => {
    const formattedDocument = formatDocument(event.target.value);
    setDocumentoTransferencia(formattedDocument);
  };

  const handleConfirmTransferVehicle = async () => {
    try {
      if (!selectedVehicle) return;
      setLoading(true);

      const response = await transferVehicle(
        token!,
        selectedVehicle.id.toString()
      );
      const idTransferencia = response.id;

      if (signed) {
        await uploadSignedTransfer(
          signed,
          idTransferencia,
          nomeTransferencia,
          1,
          documentoTransferencia,
          token!
        );
      }

      toast.success("Veículo transferido com sucesso!");
      handleCloseModal();

      const data = await getTransferVehicles(token!, 1);
      setVehicles(data.data);
      setOriginalData(data.data);
      setCurrentPage(data.current_page);
      setTotalPages(data.last_page);
      setTotalVehicles(data.total);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage);
      } else {
        toast.error("Erro ao transferir veículo.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado");
    }

    const timeoutId = setTimeout(async () => {
      try {
        const data = await searchVehiclesTransfer(
          token,
          searchValue,
          currentPage
        );
        setVehicles(data.data.length > 0 ? data.data : []);
        setTotalPages(data.last_page);
        setTotalVehicles(data.total);
      } catch (error) {
        setVehicles([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue, token, originalData, currentPage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setCurrentPage(1);
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleCompanyChange = (event: any, newValue: any) => {
    setSelectedCompany(newValue);
    if (newValue) {
      setFilteredLocations(newValue.locais);
    } else {
      setFilteredLocations([]);
    }
    setSelectedLocation(null);
  };

  const videoConstraints = {
    facingMode: isFrontFacing ? "user" : "environment",
  };

  const handleOpenCamera = () => {
    setIsCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
  };

  const toggleCameraFacing = () => {
    setIsFrontFacing(!isFrontFacing);
  };

  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImages((prevImages) => [...prevImages, imageSrc]);
      }
      setIsCameraOpen(false);
    }
  };

  const handleRemoveCapturedImage = (index: number) => {
    setCapturedImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleCloseModalTransfer = () => {
    setTransferModalHist(false);
    setCompanies([]);
    setSelectedCompany(null);
    setDescricao("");
    setCapturedImages([]);
  };

  const handleTransferVehicle = async () => {
    if (selectedVehicle && selectedCompany && selectedLocation) {
      try {
        setLoading(true);

        const response = await transferLocalVehicle(
          selectedVehicle.id,
          selectedCompany.id,
          selectedLocation.id,
          descricao,
          token!
        );

        const idTransferencia = response.id;

        if (capturedImages.length > 0) {
          const uploadPromises = capturedImages.map(async (image) => {
            try {
              await uploadCapturedImages(image, idTransferencia, token!);
            } catch (error) {
              console.error("Erro ao fazer upload da imagem:", error);
            }
          });

          if (signed) {
            await uploadSignedTransfer(
              signed,
              idTransferencia,
              nomeTransferencia,
              1,
              documentoTransferencia,
              token!
            );
          }

          await Promise.all(uploadPromises);
        }

        toast.success("Veículo transferido com sucesso!");
        handleCloseModalTransfer();
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          const errorMessage = error.response.data.error;
          toast.error(errorMessage);
        } else {
          toast.error("Erro ao transferir veículo.");
        }
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("Selecione uma empresa e um local.");
    }
  };

  const handleOpenModalTransfer = async (vehicle: GetVeiculosTransferencia) => {
    setTransferModalHist(true);
    setSelectedVehicle(vehicle);

    if (vehicle) {
      try {
        const companiesData = await getEmpresaLocais(token!, vehicle.id);
        setCompanies(companiesData.empresas);
        setHistorico(companiesData.historico);
      } catch (error) {
        toast.error("Erro ao buscar empresas.");
        console.error("Erro ao buscar empresas:", error);
      }
    }
  };

  const handleOpenModaHistoricoFotos = (fotos: string[]) => {
    setHistoricoFotos(fotos);
    setOpenModalHistoricoFotos(true);
  };

  const handleCloseModalHistoricoFotos = () => {
    setOpenModalHistoricoFotos(false);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }} p={2}>
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <Toolbar />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              margin: { xs: "10px 0", sm: 0 },
            }}
          >
            {loading ? (
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={50}
                sx={{
                  borderRadius: 2,
                  mr: 5,
                }}
              />
            ) : (
              <TextField
                placeholder="Pesquisar por Chassi/Modelo/Cliente"
                fullWidth
                sx={{
                  margin: { xs: "0 0 10px 0", sm: 0 },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#303030" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor: "white",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#303030",
                    },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                value={searchValue}
                onChange={handleSearchChange}
              />
            )}
          </Box>
          <Grid item xs={6}>
            {loading ? (
              <Skeleton
                variant="rectangular"
                height={30}
                width={140}
                sx={{
                  borderRadius: 2,
                  bgcolor: "grey.400",
                  marginTop: 2,
                }}
              />
            ) : (
              <Typography
                variant="overline"
                sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: 500 }}
              >
                {`Total de veículos: ${totalVehicles ?? 0}`}
              </Typography>
            )}
          </Grid>
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Box>
          <Grid container spacing={2}>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <Grid item xs={12} key={index} mt={2}>
                  <Skeleton
                    variant="rectangular"
                    height={150}
                    sx={{ borderRadius: 5, bgcolor: "grey.400" }}
                  />
                </Grid>
              ))
            ) : Array.isArray(vehicles) && vehicles.length === 0 ? (
              <Typography
                variant="h6"
                mt={12}
                sx={{ marginLeft: "auto", marginRight: "auto" }}
              >
                Nenhum veículo para transferência encontrado.
              </Typography>
            ) : (
              vehicles.map((vehicle) => (
                <Grid item xs={12} key={vehicle.id} mt={2}>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      cursor: "pointer",
                      marginBottom: 1,
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DirectionsCarFilledOutlined
                        sx={{
                          fontSize: { xs: 70, sm: 60, md: 80 },
                        }}
                      />
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{ border: 1, ml: 2 }}
                      />
                      <Grid container spacing={1} wrap="nowrap" ml={0.5}>
                        <Grid item xs={12} sm={12} md={12}>
                          <Typography
                            sx={{ typography: { sm: "h5", xs: "caption" } }}
                          >
                            V.I.N: {vehicle.chassi_veiculo}
                          </Typography>
                          <Typography
                            sx={{ typography: { sm: "h5", xs: "caption" } }}
                          >
                            Modelo: {vehicle.descricao_modelo}
                          </Typography>
                          <Typography
                            sx={{ typography: { sm: "h5", xs: "caption" } }}
                          >
                            Cor: {vehicle.descricao_cor}
                          </Typography>
                          <Typography
                            sx={{ typography: { sm: "h5", xs: "caption" } }}
                          >
                            Empresa: {vehicle.empresa_nome}
                          </Typography>
                          {vehicle.nome_cliente && (
                            <Typography
                              sx={{ typography: { sm: "h5", xs: "caption" } }}
                            >
                              Cliente: {vehicle.nome_cliente}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                      {shouldShowTransferButton ? (
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: "#3D62F5",
                            ":hover": { bgcolor: "#5c5c5c" },
                            height: 120,
                          }}
                          onClick={() => handleOpenModal(vehicle)}
                        >
                          <KeyboardDoubleArrowRight sx={{ fontSize: 30 }} />
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: "#3D62F5",
                            ":hover": { bgcolor: "#5c5c5c" },
                            height: 120,
                          }}
                          onClick={() => handleOpenModalTransfer(vehicle)}
                        >
                          <KeyboardDoubleArrowRight sx={{ fontSize: 30 }} />
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Box>

      <Modal
        open={transferModalHist}
        closeAfterTransition
        disableEscapeKeyDown
        disableEnforceFocus
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={transferModalHist}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleCloseModalTransfer}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
            <Typography variant="h5" component="h4">
              Transferência de Veículo
            </Typography>
            <Typography variant="body1" mt={1}>
              <strong>Chassi:</strong> {selectedVehicle?.chassi_veiculo}
            </Typography>
            <Typography variant="body1">
              <strong>Modelo:</strong> {selectedVehicle?.descricao_modelo}
            </Typography>
            {selectedVehicle?.nome_cliente && (
              <Typography variant="body1">
                <strong>Cliente:</strong> {selectedVehicle?.nome_cliente}
              </Typography>
            )}

            <Divider sx={{ marginTop: 1 }} />
            {!selectedVehicle?.entrega_veiculo && (
              <Box>
                <Autocomplete
                  options={companies}
                  getOptionLabel={(option) => option.nome_empresa}
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  noOptionsText="Nenhuma empresa encontrada!"
                  PopperComponent={CustomPopper}
                  componentsProps={{
                    popper: {
                      style: { zIndex: 1301 },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione a Empresa de Destino"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                    />
                  )}
                />

                {selectedCompany && (
                  <>
                    <Autocomplete
                      options={filteredLocations}
                      getOptionLabel={(option) => option.descricao_local}
                      value={selectedLocation}
                      onChange={(event, newValue) =>
                        setSelectedLocation(newValue)
                      }
                      noOptionsText={"Nenhum local de estoque cadastrado!"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selecione o Local de Armazenagem"
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          disabled={!selectedCompany}
                        />
                      )}
                    />
                    <TextField
                      label="Observação"
                      variant="outlined"
                      multiline
                      rows={2}
                      fullWidth
                      value={descricao}
                      onChange={(event) => setDescricao(event.target.value)}
                      sx={{ mt: 2 }}
                    />
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        component="label"
                        color="primary"
                        sx={{ color: "white", mr: 1 }}
                        onClick={handleOpenCamera}
                      >
                        <CameraAlt />
                      </Button>
                    </Box>
                    {capturedImages.length > 0 && (
                      <Grid container spacing={2} mt={1}>
                        {capturedImages.map((image, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "56.25%",
                                borderRadius: "8px",
                                overflow: "hidden",
                              }}
                            >
                              <IconButton
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  color: "white",
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                  ":hover": {
                                    backgroundColor: "rgba(0,0,0,0.8)",
                                  },
                                  zIndex: 1,
                                }}
                                onClick={() => handleRemoveCapturedImage(index)}
                              >
                                <Close />
                              </IconButton>
                              <img
                                src={image}
                                alt={`Captured ${index}`}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </>
                )}
                <Modal open={isCameraOpen}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 800,
                      height: "80%",
                      maxHeight: 700,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={handleCloseCamera}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/png"
                      videoConstraints={videoConstraints}
                      style={{
                        width: "100%",
                        height: "auto",
                        marginBottom: 50,
                      }}
                    />
                    <Grid
                      container
                      spacing={2}
                      sx={{ mt: { xs: -13, sm: -10, md: -6 } }}
                    >
                      <Grid item xs={12} sm={6} md={6}>
                        <Button
                          variant="contained"
                          onClick={capture}
                          sx={{
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            backgroundColor: "#303030",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                          }}
                        >
                          Capturar Imagem
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <IconButton
                          onClick={toggleCameraFacing}
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            backgroundColor: "#303030",
                            color: "white",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                            borderRadius: 1,
                          }}
                        >
                          {isFrontFacing ? <CameraFront /> : <CameraRear />}
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Modal>
                <Button
                  variant="contained"
                  sx={{
                    width: "100%",
                    backgroundColor: "#303030",
                    marginTop: 2,
                  }}
                  disabled={!selectedCompany}
                  onClick={handleTransferVehicle}
                >
                  Transferir
                </Button>
              </Box>
            )}

            <Typography variant="h6" component="h6" sx={{ mt: 4 }}>
              Histórico de Transferências
            </Typography>

            {historico.length === 0 ? (
              <Typography>
                Não há registros de transferências desse veículo
              </Typography>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Responsável</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Origem</TableCell>
                      <TableCell>Destino</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historico.map((transferencias, index) => (
                      <React.Fragment key={index}>
                        {transferencias.map(
                          (
                            transferencia: Transferencia,
                            innerIndex: number
                          ) => (
                            <React.Fragment key={innerIndex}>
                              <TableRow
                                onClick={() => handleRowClick(innerIndex)}
                              >
                                <TableCell>
                                  {transferencia.Responsavel}
                                </TableCell>
                                <TableCell>{transferencia.data}</TableCell>
                                <TableCell>{transferencia.origem}</TableCell>
                                <TableCell>{transferencia.destino}</TableCell>
                                <TableCell>
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                  >
                                    {openRow === innerIndex ? (
                                      <KeyboardArrowUp />
                                    ) : (
                                      <KeyboardArrowDown />
                                    )}
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  style={{ paddingBottom: 0, paddingTop: 0 }}
                                  colSpan={6}
                                >
                                  <Collapse
                                    in={openRow === innerIndex}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box
                                      margin={1}
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary" }}
                                        mt={1}
                                        style={{ flex: 1 }}
                                      >
                                        <strong>Observação:</strong>{" "}
                                        <span
                                          style={{
                                            color: "#5f5f5f",
                                            fontSize: "0.9rem",
                                            whiteSpace: "pre-wrap",
                                            maxWidth: "300px",
                                            overflowWrap: "break-word",
                                            display: "block",
                                          }}
                                        >
                                          {
                                            transferencia.observacao_historico_transferencia_veiculo
                                          }
                                        </span>
                                      </Typography>
                                      <Button
                                        sx={{
                                          alignSelf: "start",
                                          alignItems: "start",
                                        }}
                                        onClick={() =>
                                          handleOpenModaHistoricoFotos(
                                            transferencia.fotos
                                          )
                                        }
                                      >
                                        Ver Fotos
                                        <Photo sx={{ marginLeft: 1 }} />
                                      </Button>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          )
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Fade>
      </Modal>

      <Modal
        open={openModalHistoricoFotos}
        onClose={handleCloseModalHistoricoFotos}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModalHistoricoFotos}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5" component="h4">
                Fotos da Transferência do Veículo
              </Typography>
              <IconButton
                size="medium"
                edge="start"
                aria-label="close"
                sx={{
                  mr: 2,
                  backgroundColor: "red",
                  color: "white",
                  ml: 2,
                  ":hover": {
                    bgcolor: "#5c5c5c",
                  },
                }}
                onClick={handleCloseModalHistoricoFotos}
              >
                <Close />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              {historicoFotos.length === 0 ? (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  mt={2}
                  textAlign={"center"}
                  ml={2}
                >
                  Não há fotos para esta transferência.
                </Typography>
              ) : (
                historicoFotos.map((foto, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <img
                      src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto}`}
                      alt={`Transferência ${index}`}
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                      }}
                    />
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        </Fade>
      </Modal>

      {selectedVehicle && (
        <Modal
          open={transferModal}
          onClose={handleCloseModal}
          closeAfterTransition
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={transferModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "80%",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <Typography variant="h5" component="h4">
                Transferência de Veículo
              </Typography>
              <Typography variant="body1" mt={1}>
                <strong>Chassi:</strong> {selectedVehicle.chassi_veiculo}
              </Typography>
              <Typography variant="body1">
                <strong>Modelo:</strong> {selectedVehicle.descricao_modelo}
              </Typography>
              {selectedVehicle.nome_cliente && (
                <Typography variant="body1">
                  <strong>Cliente:</strong> {selectedVehicle.nome_cliente}
                </Typography>
              )}

              <Divider sx={{ marginTop: 1 }} />

              <Box>
                <TextField
                  label="Informe seu Nome e Empresa"
                  variant="outlined"
                  required
                  fullWidth
                  value={nomeTransferencia}
                  onChange={(event) => setNomeTransferencia(event.target.value)}
                  sx={{ mt: 2 }}
                />
                <TextField
                  label="Documento"
                  variant="outlined"
                  required
                  fullWidth
                  value={documentoTransferencia}
                  onChange={handleDocumentoChange}
                  sx={{ mt: 2 }}
                />
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      color: "white",
                      backgroundColor: signed ? "green" : "primary",
                    }}
                    onClick={handleOpenSign}
                  >
                    {signed
                      ? "Transferência Assinada"
                      : "Assinar Transferência"}
                  </Button>
                </Box>
                <Modal open={isSignOpen}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 800,
                      height: "80%",
                      maxHeight: 350,
                    }}
                  >
                    <IconButton
                      onClick={handleCloseSign}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                    <Typography
                      variant="h6"
                      color="black"
                      height={50}
                      sx={{
                        paddingTop: "13px",
                      }}
                    >
                      Assinatura do Responsável
                    </Typography>

                    {signed ? (
                      <>
                        <img
                          src={signed}
                          alt="Assinatura"
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            marginBottom: 45,
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleClearSignature}
                          sx={{
                            mt: 7,
                            display: "block",
                            margin: "0 auto",
                            backgroundColor: "#303030",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                          }}
                        >
                          Apagar Assinatura
                        </Button>
                      </>
                    ) : (
                      <>
                        <Box
                          mb={5}
                          display="flex"
                          alignItems="center"
                          borderBottom={2}
                        >
                          <CanvasDraw
                            brushColor={"black"}
                            brushRadius={1}
                            canvasWidth={1200}
                            canvasHeight={200}
                            hideInterface={true}
                            hideGrid={true}
                            ref={canvasRef}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 5,
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={handleExport}
                            sx={{
                              backgroundColor: "#303030",
                              ":hover": {
                                bgcolor: "#5c5c5c",
                              },
                              marginRight: 2,
                            }}
                          >
                            Salvar Assinatura
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              canvasRef.current.clear();
                            }}
                            sx={{
                              backgroundColor: "#FF0000",
                              ":hover": {
                                bgcolor: "#AA0000",
                              },
                            }}
                          >
                            Limpar Assinatura
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </Modal>
                <Button
                  variant="contained"
                  sx={{
                    width: "100%",
                    backgroundColor: "#303030",
                    marginTop: 2,
                  }}
                  disabled={
                    !nomeTransferencia || !documentoTransferencia || !signed
                  }
                  onClick={handleConfirmTransferVehicle}
                >
                  Aprovar Transferência
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
      )}
    </ThemeProvider>
  );
};

export default VehicleTransfer;
